import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import algoliasearch, { type SearchIndex } from "algoliasearch";
import type { FacilityHit, GlobalHit, PersonHit } from "@/components/AlgoliaSearch";
import type { MentionExpandContext, MentionSearchResult, MentionUser } from "@/components/AtMention/types";
import useCapabilities from "@/hooks/useCapabilities";
import useCurrentUser from "@/hooks/useCurrentUser";
import useMentionSearch from "@/hooks/useMentionSearch";
import { parseStateFromLocation } from "@/lib/geo/pgaSectionStates";
import {
  buildAlgoliaMentionQuery,
  facilityMatchesQuery,
  noteTitleMatchesQuery,
  personNameMatchesQuery,
  workplaceMatchesQuery
} from "@/lib/geo/quickAddMentionMatch";
import { rankQuickAddMentions, type GeoProximityContext } from "@/lib/geo/rankQuickAddMentions";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import {
  EVENTS_FOR_REFERENCE_SEARCH_QUERY,
  type EventsForReferenceSearchResponse,
  type EventsForReferenceSearchVariables
} from "@/pages/tasks/[taskId]/_hooks/eventsForReferenceSearchQuery";
import { useReferencePickerItems } from "@/pages/tasks/[taskId]/_hooks/useReferencePickerItems";
import type { Section } from "@/types";
import {
  ME_CAREER_CONSULTANT_SECTIONS_QUERY,
  QUICK_ADD_PERSON_PROGRAM_STATUS_QUERY,
  type MeCareerConsultantSectionsResponse,
  type QuickAddPersonProgramStatusResponse,
  type QuickAddPersonProgramStatusVariables
} from "./queries";
import {
  facilityMentionFromPersonHit,
  mapAlgoliaHitToMention,
  mapEventToMention,
  mapPickerItemToMention,
  QUICK_ADD_MENTION_OVERLAY,
  teammateMention,
  toLookupContext
} from "./quickAddMentionMappers";
import {
  filterHitsMissingProgramStatus,
  resolvePersonStatusLookup,
  uniqueAlgoliaPersonPgaIds
} from "./quickAddPersonProgramStatus";

const EVENT_SEARCH_LIMIT = 10;
const ALGOLIA_HITS_PER_PAGE = 15;
const ALGOLIA_FACILITY_PREFIX_HITS = 30;
const PERSON_NAME_ATTRIBUTES = ["name", "preferred_name", "pga_id", "phone", "phones", "legacy_id", "email"];
const FACILITY_ATTRIBUTES = ["name", "facility_name", "legacy_id", "section_name"];
const PERSON_WORKPLACE_ATTRIBUTES = ["facility_name"];
const MERGED_ATTRIBUTES = [...PERSON_NAME_ATTRIBUTES, "facility_name", "section_name"];

function createSearchIndex() {
  const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
  const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
  const indexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
  if (!appId || !apiKey || !indexName) return null;
  return algoliasearch(appId, apiKey).initIndex(indexName);
}

function mentionDisplayName(user: MentionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
}

function isPersonHit(hit: GlobalHit): hit is PersonHit {
  return hit.object_type === "Person";
}

function isFacilityHit(hit: GlobalHit): hit is FacilityHit {
  return hit.object_type === "Facility";
}

function facilityHitName(hit: FacilityHit): string {
  return hit.facility_name || hit.name || "";
}

function mergeHits(results: Array<{ hits: GlobalHit[] }>, byId = new Map<string, GlobalHit>()): Map<string, GlobalHit> {
  for (const result of results) {
    for (const hit of result.hits) {
      byId.set(hit.objectID, hit);
    }
  }
  return byId;
}

async function searchWorkplacePeopleAtMatchingFacilities(
  searchIndex: SearchIndex,
  query: string,
  hits: Iterable<GlobalHit>
): Promise<GlobalHit[]> {
  const names = [
    ...new Set(
      [...hits]
        .filter(isFacilityHit)
        .map(facilityHitName)
        .filter((name) => facilityMatchesQuery(name, query))
    )
  ].slice(0, 2);
  if (names.length === 0) return [];
  const settled = await Promise.allSettled(
    names.map((name) => {
      const shortName = name.split(/\s+/).filter(Boolean).slice(0, 2).join(" ") || name;
      return searchIndex.search<GlobalHit>(shortName, {
        hitsPerPage: ALGOLIA_HITS_PER_PAGE,
        filters: "object_type:Person",
        restrictSearchableAttributes: PERSON_WORKPLACE_ATTRIBUTES
      });
    })
  );
  return settled.flatMap((result) => (result.status === "fulfilled" ? result.value.hits : []));
}

function isCompactAcronymQuery(query: string): boolean {
  const compact = query.trim().toLowerCase();
  return !compact.includes(" ") && /^[a-z]{3,6}$/.test(compact);
}

export async function searchQuickAddHits(searchIndex: SearchIndex, query: string): Promise<GlobalHit[]> {
  const { primaryQuery, optionalWords, extraQueries, facilityPrefixQueries, spacedInitialsQuery } =
    buildAlgoliaMentionQuery(query);
  const optional = optionalWords?.length ? { optionalWords } : {};
  const skipWorkplaceByAcronym = isCompactAcronymQuery(query);
  const settled = await Promise.allSettled([
    searchIndex.search<GlobalHit>(primaryQuery, {
      hitsPerPage: ALGOLIA_HITS_PER_PAGE,
      filters: "object_type:Person",
      restrictSearchableAttributes: PERSON_NAME_ATTRIBUTES,
      ...optional
    }),
    searchIndex.search<GlobalHit>(primaryQuery, {
      hitsPerPage: ALGOLIA_HITS_PER_PAGE,
      filters: "object_type:Facility",
      restrictSearchableAttributes: FACILITY_ATTRIBUTES,
      ...optional
    }),
    ...(skipWorkplaceByAcronym
      ? []
      : [
          searchIndex.search<GlobalHit>(primaryQuery, {
            hitsPerPage: ALGOLIA_HITS_PER_PAGE,
            filters: "object_type:Person",
            restrictSearchableAttributes: PERSON_WORKPLACE_ATTRIBUTES,
            ...optional
          })
        ]),
    ...extraQueries.map((extraQuery) =>
      searchIndex.search<GlobalHit>(extraQuery, {
        hitsPerPage: ALGOLIA_HITS_PER_PAGE,
        filters: extraQuery.length <= 3 ? "object_type:Facility" : "object_type:Person OR object_type:Facility",
        restrictSearchableAttributes: extraQuery.length <= 3 ? FACILITY_ATTRIBUTES : MERGED_ATTRIBUTES
      })
    ),
    ...facilityPrefixQueries.map((prefixQuery) =>
      searchIndex.search<GlobalHit>(prefixQuery, {
        hitsPerPage: ALGOLIA_FACILITY_PREFIX_HITS,
        filters: "object_type:Facility",
        restrictSearchableAttributes: FACILITY_ATTRIBUTES
      })
    ),
    ...(spacedInitialsQuery
      ? [
          searchIndex.search<GlobalHit>(spacedInitialsQuery, {
            hitsPerPage: ALGOLIA_FACILITY_PREFIX_HITS,
            filters: "object_type:Facility",
            restrictSearchableAttributes: FACILITY_ATTRIBUTES,
            queryType: "prefixAll"
          })
        ]
      : [])
  ]);

  const results = settled.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
  const byId = mergeHits(results);
  const workplaceAtMatch = await searchWorkplacePeopleAtMatchingFacilities(searchIndex, query, byId.values());
  mergeHits([{ hits: workplaceAtMatch }], byId);
  return [...byId.values()];
}

function withSyntheticFacility(hits: GlobalHit[], mapped: MentionUser[], query: string): MentionUser[] {
  const hasMatchingFacility = mapped.some(
    (user) => user.kind === "facility" && facilityMatchesQuery(mentionDisplayName(user), query)
  );
  if (hasMatchingFacility) return mapped;
  for (const hit of hits) {
    if (!isPersonHit(hit) || !hit.facility_name || !facilityMatchesQuery(hit.facility_name, query)) continue;
    const synthetic = facilityMentionFromPersonHit(hit);
    if (synthetic) return [synthetic, ...mapped];
  }
  return mapped;
}

function keepRecord(user: MentionUser, query: string): boolean {
  if (user.kind === "facility") {
    return (
      facilityMatchesQuery(mentionDisplayName(user), query) || facilityMatchesQuery(user.facilityName || "", query)
    );
  }
  if (user.kind === "person") {
    if (personNameMatchesQuery(user, query)) return true;
    return workplaceMatchesQuery(user.facilityName, query);
  }
  if (user.kind === "event") {
    const full = mentionDisplayName(user).toLowerCase();
    const compactQuery = query.trim().toLowerCase();
    return full.includes(compactQuery) || compactQuery.split(/\s+/).every((token) => !token || full.includes(token));
  }
  return true;
}

function interleaveNotes(ranked: MentionUser[], notes: MentionUser[]): MentionUser[] {
  if (notes.length === 0) return ranked;
  const index = ranked.findIndex((user) => user.kind === "person" || user.kind === "facility" || user.kind === "event");
  if (index < 0) return [...ranked, ...notes].slice(0, 12);
  return [...ranked.slice(0, index + 1), ...notes, ...ranked.slice(index + 1)].slice(0, 12);
}

type PendingSelect = { context: MentionExpandContext; query: string };

export default function useQuickAddMentionSearch(query: string): MentionSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [pendingSelect, setPendingSelect] = useState<PendingSelect | null>(null);
  const [algoliaHits, setAlgoliaHits] = useState<GlobalHit[]>([]);
  const [algoliaLoading, setAlgoliaLoading] = useState(false);
  const searchIndex = useMemo(() => createSearchIndex(), []);

  if (pendingSelect && pendingSelect.query !== query) {
    setPendingSelect(null);
  }
  const pendingContext = pendingSelect?.query === query ? pendingSelect.context : null;

  useEffect(() => {
    if (!query) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { users: teammateUsers, isLoading: teammatesLoading } = useMentionSearch(query);

  const { sectionLegacyIds } = useCapabilities();
  const {
    user: { location }
  } = useCurrentUser();

  const skipRankingQueries = !debouncedQuery;

  const { data: consultantData } = useQuery<MeCareerConsultantSectionsResponse>(ME_CAREER_CONSULTANT_SECTIONS_QUERY, {
    skip: skipRankingQueries,
    errorPolicy: "all",
    fetchPolicy: "cache-first"
  });

  const { data: sectionsData } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: skipRankingQueries || sectionLegacyIds.length === 0,
    fetchPolicy: "cache-first"
  });

  const { data: eventsData, loading: eventsLoading } = useQuery<
    EventsForReferenceSearchResponse,
    EventsForReferenceSearchVariables
  >(EVENTS_FOR_REFERENCE_SEARCH_QUERY, {
    variables: { query: { title: debouncedQuery }, first: EVENT_SEARCH_LIMIT },
    skip: !debouncedQuery || !!pendingContext
  });

  const personPgaIds = useMemo(() => uniqueAlgoliaPersonPgaIds(algoliaHits), [algoliaHits]);
  const {
    data: personStatusData,
    loading: personStatusLoading,
    error: personStatusError
  } = useQuery<QuickAddPersonProgramStatusResponse, QuickAddPersonProgramStatusVariables>(
    QUICK_ADD_PERSON_PROGRAM_STATUS_QUERY,
    {
      variables: { input: { pgaIds: personPgaIds }, first: Math.max(personPgaIds.length, 1) },
      skip: personPgaIds.length === 0 || !debouncedQuery,
      fetchPolicy: "cache-first",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true
    }
  );

  const personStatusByPgaId = useMemo(
    () =>
      resolvePersonStatusLookup({
        personPgaIdCount: personPgaIds.length,
        loading: personStatusLoading,
        data: personStatusData,
        error: personStatusError
      }),
    [personPgaIds.length, personStatusData, personStatusError, personStatusLoading]
  );

  const statusFilteredHits = useMemo(
    () => filterHitsMissingProgramStatus(algoliaHits, personStatusByPgaId),
    [algoliaHits, personStatusByPgaId]
  );

  useEffect(() => {
    if (!debouncedQuery || !searchIndex) {
      setAlgoliaHits([]);
      setAlgoliaLoading(false);
      return;
    }

    let cancelled = false;
    setAlgoliaLoading(true);
    searchQuickAddHits(searchIndex, debouncedQuery)
      .then((hits) => {
        if (!cancelled) setAlgoliaHits(hits);
      })
      .catch(() => {
        if (!cancelled) setAlgoliaHits([]);
      })
      .finally(() => {
        if (!cancelled) setAlgoliaLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, searchIndex]);

  const geoContext: GeoProximityContext = useMemo(() => {
    const assignedIds = new Set(sectionLegacyIds);
    const assignedSectionNames = (sectionsData?.sections ?? [])
      .filter(
        (section) => Boolean(section.legacyId && assignedIds.has(section.legacyId)) || assignedIds.has(section.id)
      )
      .map((section) => section.name);
    return {
      consultantSectionNames: consultantData?.careerConsultantSections?.map((section) => section.name) ?? [],
      assignedSectionNames,
      userLocationState: parseStateFromLocation(location)
    };
  }, [consultantData, location, sectionLegacyIds, sectionsData]);

  const recordMentions = useMemo(() => {
    const mapped = statusFilteredHits.map(mapAlgoliaHitToMention).filter((item): item is MentionUser => item !== null);
    const withFacility = withSyntheticFacility(statusFilteredHits, mapped, query);
    return withFacility.filter((user) => keepRecord(user, query));
  }, [query, statusFilteredHits]);

  const rankedBase = useMemo(() => {
    const teammates = teammateUsers
      .map(teammateMention)
      .filter((user) => personNameMatchesQuery(user, query) || workplaceMatchesQuery(user.facilityName, query));
    const events = (eventsData?.events?.edges ?? [])
      .map(({ node }) => mapEventToMention(node))
      .filter((user) => keepRecord(user, query));
    return rankQuickAddMentions([...teammates, ...recordMentions, ...events], query, geoContext);
  }, [eventsData, geoContext, query, recordMentions, teammateUsers]);

  const prefetchParent = useMemo(
    () =>
      rankedBase.find(
        (user) => user.expandContext && (user.kind === "person" || user.kind === "facility" || user.kind === "event")
      ) ?? null,
    [rankedBase]
  );

  const lookupContext = useMemo(() => {
    const prefetch = prefetchParent?.kind === "person" ? null : (prefetchParent?.expandContext ?? null);
    const context = pendingContext ?? prefetch;
    return context ? toLookupContext(context) : null;
  }, [pendingContext, prefetchParent]);

  const { items: pickerItems, loading: pickerLoading } = useReferencePickerItems(lookupContext);

  const autoInsertUser = useMemo(() => {
    if (!pendingContext) return null;
    const parent = pickerItems.find((item) => {
      if (pendingContext.type === "Person") return item.__typename === "CRMPerson";
      if (pendingContext.type === "Facility") return item.__typename === "CRMTaskFacility";
      return item.__typename === "CRMEvent";
    });
    return parent ? mapPickerItemToMention(parent) : null;
  }, [pendingContext, pickerItems]);

  const users = useMemo(() => {
    if (pendingContext) return rankedBase;
    const notes = pickerItems.filter((item) => item.__typename === "CRMNote");
    const uniqueNotes = [...new Map(notes.map((note) => [note.id, note])).values()];
    const matching = uniqueNotes.filter((note) => noteTitleMatchesQuery(note.displayName, query));
    const chosen = (matching.length > 0 ? matching : uniqueNotes.slice(0, 3)).slice(0, 5);
    const noteMentions = chosen.map(mapPickerItemToMention).filter((item): item is MentionUser => item !== null);
    return interleaveNotes(rankedBase, noteMentions);
  }, [pendingContext, pickerItems, query, rankedBase]);

  const resolveSelect = useCallback(
    (user: MentionUser) => {
      if (user.selectAction === "expand" && user.expandContext) {
        setPendingSelect({ context: user.expandContext, query });
        return false;
      }
      if (pendingContext) setPendingSelect(null);
      return true;
    },
    [pendingContext, query]
  );

  const dismiss = useCallback(() => {
    if (pendingSelect) {
      setPendingSelect(null);
      return false;
    }
    return true;
  }, [pendingSelect]);

  const personInsertPending = pendingContext?.type === "Person" && !autoInsertUser;
  const personLookupFailed = Boolean(personInsertPending && !pickerLoading);
  const isLoading = personInsertPending
    ? pickerLoading
    : (teammatesLoading || algoliaLoading || eventsLoading || personStatusLoading) && users.length === 0;

  const overlay = personLookupFailed
    ? { ...QUICK_ADD_MENTION_OVERLAY, error: "Could not attach that person. Pick again." }
    : QUICK_ADD_MENTION_OVERLAY;

  return {
    users,
    isLoading,
    resolveSelect,
    dismiss,
    overlay,
    autoInsertUser
  };
}
